import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonService } from '../../../core/services/common.service';
import { HttpClientService } from '../../../core/services/http-client.service';
import { ToastService } from '../../../core/services/toast.service';


interface WinnerTick {
  city: string;
  phone: string;
  gift: string;
  minsAgo: number;
}

type LoginStage = 'phone' | 'otp' | 'code';

@Component({
  selector: 'app-spin-landing',
  templateUrl: './spin-landing.component.html',
  styleUrls: ['./spin-landing.component.css']
})
export class SpinLandingComponent implements OnInit, OnDestroy {
  isLoggedIn: boolean = false;

  // Real winners feed — populated by loadWinners() when the backend endpoint is available.
  // Keeps the ticker hidden if the list is empty, so we never show fake data.
  winners: WinnerTick[] = [];
  tickIndex: number = 0;
  private tickTimer: any;

  // Login modal state
  loginOpen: boolean = false;
  loginStage: LoginStage = 'phone';
  phone: string = '';
  otp: string = '';
  invoiceCode: string = '';
  referenceId: string = '';
  phoneErr: boolean = false;
  busy: boolean = false;
  resendSeconds: number = 0;
  private resendTimer: any;

  constructor(
    private router: Router,
    private common: CommonService,
    private http: HttpClientService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.isLoggedIn = this.common.isLoggedIn();
    if (this.isLoggedIn) {
      this.router.navigateByUrl('/spin');
      return;
    }
    this.loadWinners();
  }

  private loadWinners(): void {
    this.http.get('store/recent-winners?limit=10').subscribe(
      (data: any) => {
        const list = (data?.response || [])
          .filter((w: any) => w && w.maskedMobile && w.giftName)
          .map((w: any) => ({
            city: '',
            phone: w.maskedMobile,
            gift: w.giftName,
            minsAgo: this.minutesAgo(w.scracthedAt)
          }));
        this.winners = list;
        this.startTicker();
      },
      () => {
        // Silently ignore — ticker stays hidden.
      }
    );
  }

  private minutesAgo(isoString: string): number {
    if (!isoString) return 0;
    const t = new Date(isoString).getTime();
    if (isNaN(t)) return 0;
    const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
    return mins;
  }

  private startTicker(): void {
    if (this.winners.length === 0) return;
    this.tickTimer = setInterval(() => {
      this.tickIndex = (this.tickIndex + 1) % this.winners.length;
    }, 3500);
  }

  ngOnDestroy(): void {
    if (this.tickTimer) clearInterval(this.tickTimer);
    if (this.resendTimer) clearInterval(this.resendTimer);
  }

  openLogin(): void {
    if (this.isLoggedIn) {
      this.router.navigateByUrl('/spin');
      return;
    }
    this.loginOpen = true;
    this.loginStage = 'phone';
    this.phoneErr = false;
  }

  closeLogin(): void {
    this.loginOpen = false;
    this.busy = false;
  }

  showCodeStage(): void {
    this.loginStage = 'code';
  }

  changeNumber(): void {
    this.loginStage = 'phone';
    this.otp = '';
    this.referenceId = '';
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendSeconds = 0;
    }
  }

  get phoneValid(): boolean {
    return /^[6-9]\d{9}$/.test(this.phone.replace(/\s+/g, ''));
  }
  get otpValid(): boolean {
    return /^\d{5}$/.test(this.otp.trim());
  }
  get maskedPhone(): string {
    const clean = this.phone.replace(/\s+/g, '');
    if (clean.length !== 10) return clean;
    return clean.slice(0, 2) + 'XXXXX' + clean.slice(7);
  }
  get resendLabel(): string {
    if (this.resendSeconds <= 0) return 'Resend OTP';
    const m = Math.floor(this.resendSeconds / 60);
    const sNum = this.resendSeconds % 60;
    const s = sNum < 10 ? '0' + sNum : '' + sNum;
    return `Resend in ${m}:${s}`;
  }

  sendOtp(): void {
    this.phoneErr = !this.phoneValid;
    if (this.phoneErr || this.busy) return;

    this.busy = true;
    const mobile = this.phone.replace(/\s+/g, '');
    this.http.get('store/otp/generateOTP', { mobile }).subscribe(
      (data: any) => {
        this.busy = false;
        const refId = data?.response?.reference_id;
        if (refId) {
          this.referenceId = refId;
          this.loginStage = 'otp';
          this.otp = '';
          this.startResendTimer();
          this.toast.success('OTP sent');
        } else {
          this.toast.error('Could not send OTP, please try again');
        }
      },
      () => {
        this.busy = false;
        this.toast.error('Could not send OTP, please try again');
      }
    );
  }

  resendOtp(): void {
    if (this.resendSeconds > 0 || this.busy) return;
    this.sendOtp();
  }

  verifyOtp(): void {
    if (!this.otpValid || this.busy) return;

    this.busy = true;
    const mobile = this.phone.replace(/\s+/g, '');
    const payload = { mobile, otp: this.otp.trim(), referenceId: this.referenceId };

    this.http.post('store/signinWithOtp', payload).subscribe(
      () => {
        // signinWithOtp only confirms the OTP is valid; fetch the customer separately
        this.fetchCustomerAndFinish(mobile);
      },
      (err: any) => {
        this.busy = false;
        const msg = err?.error?.message || err?.message || 'Invalid OTP, please try again';
        this.toast.error(msg);
      }
    );
  }

  private fetchCustomerAndFinish(mobile: string): void {
    this.http.get('store/checkmobile/' + mobile).subscribe(
      (data: any) => {
        this.busy = false;
        const payload = data?.response;
        const customer = payload?.customer;
        if (!payload?.exists || !customer || !customer.id) {
          this.toast.error('No account found for this mobile. Please purchase first to earn a spin.');
          return;
        }
        this.common.setStorage('customer', customer);
        this.common.setLoggedIn(true);
        this.toast.success('Logged in');
        this.closeLogin();
        this.router.navigateByUrl('/spin');
      },
      () => {
        this.busy = false;
        this.toast.error('Signed in but could not load your profile — please retry');
      }
    );
  }

  submitInvoiceCode(): void {
    if (!this.invoiceCode.trim()) {
      this.toast.error('Please enter the spin code from your invoice');
      return;
    }
    this.toast.info('Invoice-code login is coming soon. Please use mobile OTP for now.');
  }

  private startResendTimer(): void {
    this.resendSeconds = 30;
    if (this.resendTimer) clearInterval(this.resendTimer);
    this.resendTimer = setInterval(() => {
      this.resendSeconds--;
      if (this.resendSeconds <= 0) clearInterval(this.resendTimer);
    }, 1000);
  }

  goToLoginOrSpin(): void {
    if (this.isLoggedIn) {
      this.router.navigateByUrl('/spin');
    } else {
      this.openLogin();
    }
  }
}
